const UAH_SYMBOL = "₴";

export type CustomerAvailability =
  | "in_stock"
  | "low_stock"
  | "preorder"
  | "out_of_stock"
  | "unknown";

export const AVAILABILITY_LABELS_UK: Record<CustomerAvailability, string> = {
  in_stock: "Є в наявності",
  low_stock: "Закінчується",
  preorder: "Під замовлення",
  out_of_stock: "Немає в наявності",
  unknown: "Уточнюйте наявність",
};

export const AVAILABILITY_SORT_RANK: Record<CustomerAvailability, number> = {
  in_stock: 0,
  low_stock: 1,
  preorder: 2,
  out_of_stock: 3,
  unknown: 4,
};

export function formatPrice(amount: number | null | undefined, currency = "UAH"): string {
  if (amount == null || !Number.isFinite(amount) || amount <= 0) {
    return "Ціна уточнюється";
  }
  const cents = Math.round(amount * 100);
  const whole = Math.floor(cents / 100);
  const frac = cents % 100;
  const wholeFormatted = whole.toLocaleString("uk-UA").replace(/\u00A0/g, " ").replace(/,/g, " ");
  const formatted = frac === 0 ? wholeFormatted : `${wholeFormatted},${String(frac).padStart(2, "0")}`;
  if (currency === "UAH") {
    return `${formatted} ₴`;
  }
  return `${formatted} ${currency}`;
}

/** Server-provided payable amount only — never treat 0 as a real price (FUNCTIONALITY-092/096). */
export function formatCustomerPayablePrice(
  product: { payable_price_uah?: number | null; price_amount?: number | null; price?: number | null },
): string {
  const amount = product.payable_price_uah ?? product.price_amount ?? product.price;
  if (amount == null || !Number.isFinite(Number(amount)) || Number(amount) <= 0) {
    return "Ціна уточнюється";
  }
  // Never render literal 0 ₴ for sellable UI.
  return formatPrice(Number(amount));
}

export function roundPriceAmount(amount: number): number {
  return Math.round(amount * 100) / 100;
}

export function normalizeAvailabilityFromStock(
  stockStatus: string | null | undefined,
): CustomerAvailability {
  const stock = String(stockStatus ?? "").toLowerCase();
  if (!stock) {
    return "unknown";
  }
  if (stock.includes("preorder") || stock.includes("під замовлення") || stock.includes("под заказ")) {
    return "preorder";
  }
  if (
    stock.includes("out") ||
    stock.includes("unavail") ||
    stock.includes("нема") ||
    stock.includes("нет") ||
    stock.includes("sold")
  ) {
    return "out_of_stock";
  }
  if (stock.includes("low") || stock.includes("limited") || stock.includes("закінч")) {
    return "low_stock";
  }
  if (stock.includes("stock") || stock.includes("наяв") || stock.includes("налич")) {
    return "in_stock";
  }
  return "unknown";
}

export function formatAvailabilityLabel(
  availability: CustomerAvailability | string | null | undefined,
  stockStatus?: string | null,
): string {
  if (availability && availability in AVAILABILITY_LABELS_UK) {
    return AVAILABILITY_LABELS_UK[availability as CustomerAvailability];
  }
  const fromStock = normalizeAvailabilityFromStock(stockStatus);
  return AVAILABILITY_LABELS_UK[fromStock];
}

/** @deprecated use formatAvailabilityLabel */
export function formatStockStatus(stockStatus: string | null | undefined): string {
  return formatAvailabilityLabel(null, stockStatus);
}

export function stockStatusLabel(
  stockStatus: string | null | undefined,
  labels: { inStock: string; outOfStock: string },
): string {
  const availability = normalizeAvailabilityFromStock(stockStatus);
  if (availability === "out_of_stock") {
    return labels.outOfStock;
  }
  if (availability === "in_stock" || availability === "low_stock") {
    return labels.inStock;
  }
  return formatAvailabilityLabel(availability, stockStatus);
}
